import { forwardRef } from "react";
import { SelectorAvatar, SelectorAvatarStack } from "./selector-avatar";
import {
  SelectorPlaceholder,
  SelectorTrigger,
  SelectorTriggerLabel,
} from "./selector-trigger";
import type { AccountSelectSelection, SelectorTriggerProps } from "./types";

export interface AccountSelectProps extends SelectorTriggerProps {
  selection?: AccountSelectSelection;
  allLabel?: string;
}

export const AccountSelect = forwardRef<HTMLButtonElement, AccountSelectProps>(
  function AccountSelect(
    {
      selection,
      placeholder = "Select account",
      allLabel = "All accounts",
      size = "md",
      ...props
    },
    ref
  ) {
    return (
      <SelectorTrigger
        ref={ref}
        placeholder={placeholder}
        size={size}
        {...props}
      >
        {!selection ? (
          <SelectorPlaceholder>{placeholder}</SelectorPlaceholder>
        ) : selection.kind === "account" ? (
          <span className="flex min-w-0 items-center gap-2">
            <SelectorAvatar account={selection.account} size={24} />
            <SelectorTriggerLabel className="text-neutral-800">
              {selection.account.label}
            </SelectorTriggerLabel>
          </span>
        ) : selection.kind === "group" ? (
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <SelectorAvatarStack
              // Sliced so the stack never adds a +N of its own: the `· N`
              // beside it is already the whole count, and two numbers with
              // different starting points contradict each other as a group
              // grows. Both sibling triggers slice for the same reason.
              accounts={(selection.group.accounts || []).slice(0, 3)}
              max={3}
              size={24}
            />
            <SelectorTriggerLabel
              className="text-neutral-800"
              suffix={
                <span className="ml-2 font-normal text-neutral-500">
                  {`· ${(selection.group.accounts || []).length}`}
                </span>
              }
            >
              {selection.group.label}
            </SelectorTriggerLabel>
          </span>
        ) : (
          <span className="flex min-w-0 items-center gap-2">
            <i
              aria-hidden
              className="fa-regular fa-diagram-project size-5 shrink-0 text-center text-[16px] leading-5 text-blue-500"
            />
            <SelectorTriggerLabel className="text-neutral-800">
              {selection.label || allLabel}
            </SelectorTriggerLabel>
          </span>
        )}
      </SelectorTrigger>
    );
  }
);
